import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { Box } from '@abyss/web/ui/Box';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  CoverageTypesCodes,
  ReverseCoverageTypesCodes,
} from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../../common/ConstantsRoutes';
import { phoneOnly } from '../../../../../common/ConstantsStyles';
import { ContentWrapper } from '../../../../../common/ContentWrapper';
import { ImageComponent } from '../../../../../common/ImageComponent';
import { LinkWithTracking } from '../../../../../common/LinkWithTracking/LinkWithTracking';
import { Breadcrumb } from '../../../../../common/PSXHeader/Breadcrumb';
import { TruncatedText } from '../../../../../common/Text';
import { dateFormatCurrent } from '../../../../../common/Utils/datesUtils/formatDate';
import { useAdobeBrowserBackButtonTrack } from '../../../../../hooks/adobeHook/useAdobeBrowserBackButtonTrack';
import { useAdobePageTrackEvent } from '../../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useChangePCPData } from '../../../../../hooks/useChangePCPData/useChangePCPData';
import { useCurrentMemberIsPreEffective } from '../../../../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { BreadcrumbSessionStorage } from '../../../../../models/BreadcrumbSessionStorage';
import { NetworkAffiliationDetails } from '../../../../../models/ProviderDetails';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../../store/useTypeaheadStore/typeaheadStore';
import {
  getFormattedPhoneNumber,
  getPrimaryPhone,
} from '../../../../../utils/phone.utils';
import { doChoosePCPStoreUpdate } from '../../../../../utils/primaryCare.utils';
import { shortenZipCode } from '../../../../../utils/providerDetails.utils';
import { capitalizeFirstLetters } from '../../../../../utils/providerNames.utils';
import { getMemberHealthCoverageType } from '../../../../../utils/providerSearch.utils';
import { getCurrentMember } from '../../../../../utils/user.utils';
import { filterNetworkAffiliations } from '../../SearchResults/searchUtils';
import {
  ChooseLocationCardsSuccessContainer,
  ChoosePCPPhoto,
  ClinicNameAddress,
  ContentContainer,
  ContentWrapperFullWidth,
  LocationWrapperFullWidth,
  LocationsDropdown,
  PCPHeaderContainer,
  SuccessCheckMark,
  SuccessMessageHeading,
  SuccessPageBackButton,
  SuccessPageYourPCPHeader,
  SuccessPageYourPCPLocationHeader,
  SuccessSelectedPCPBox,
} from './ChoosePCPSuccess.styled';
import { SuccesssMapViewWrap } from './SuccessMapViewWrap';

export const ChoosePCPSuccess = () => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const { navigate } = useRouter();
  const { t } = useTranslation();
  const currentMember = getCurrentMember();
  const isPreEffective = useCurrentMemberIsPreEffective();
  const medicalCoverage = getMemberHealthCoverageType(
    currentMember?.eligibility,
    CoverageTypesCodes.MEDICAL
  );
  const isIFPEarlyPay = currentMember?.isIFPEarlyPay;

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const {
    setPCPSearchState,
    dependentSeqNbr,
    formattedProviderName,
    pcpEffectiveDate,
    selectedLocation,
    selectedNetworkAffiliationId,
    providerType,
    primarySpeciality,
    imageLocation,
  } = useChangePCPData();
  const preEffectivePcpDate = dateFormatCurrent(
    medicalCoverage?.coverageEffDateRange?.startDate
  );

  const choosePCPSuccess: string = 'choose pcp success';
  const choosePCPSuccessRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const uiMessaging = useLagoon('ui-messaging')();
  const featureFlags = useLagoon('feature-flags')();
  const showPcpLagTimeMessage = find(featureFlags, {
    key: ConstantsLagoon.FEATURE_FLAGS.SHOW_PCP_LAG_TIME_SUCCESS,
  })?.active;

  const pcpSuccessMessage = find(uiMessaging, {
    key: ConstantsLagoon.PCP_CHANGE_MESSAGE.PCP_SUCCESS_MESSAGE,
  });
  const { adobeBrowserBackButtonTrack } = useAdobeBrowserBackButtonTrack();
  const [viewMapEnhancementsFlag, removeTruncatedDataCard] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_TRUNCATION_DATACARD,
  ]);

  const ContentWrapperComponent = viewMapEnhancementsFlag
    ? ContentWrapperFullWidth
    : ContentWrapper;

  const LocationWrapperComponent = viewMapEnhancementsFlag
    ? LocationWrapperFullWidth
    : ContentWrapper;

  const handleBackToFindCare = () => {
    adobeLinkTrackEvent({
      name: 'back to find care',
      location: `body:${choosePCPSuccess}`,
      type: 'internal',
    });

    const selectedCoverageType =
      find(Constants.MOCK_CARE_CATEGORIES, {
        value: Constants.CHIPS_CATEGORIES.PRIMARY_CARE,
      })?.coverageType || CoverageTypesCodes.MEDICAL;

    navigate(
      `/?chipValue=${Constants.CHIPS_CATEGORIES.PRIMARY_CARE}&coverageType=${ReverseCoverageTypesCodes[selectedCoverageType]}`
    );
  };

  const handleBackToChangeLocation = () => navigate(-1);

  const handleBackToChangePCP = () => {
    const urlParams = doChoosePCPStoreUpdate(
      dependentSeqNbr,
      chipStore,
      setTypeaheadSearchStore,
      setPCPSearchState
    );

    navigate(
      `${
        isIFPEarlyPay
          ? ConstantsRoutes.GUEST_PROVIDER_SEARCH_RESULTS.path
          : ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path
      }${urlParams}`
    );
  };

  const selectedProviderLocation = selectedLocation
    ? JSON.parse(selectedLocation)
    : {};

  const selectedNetworkAffiliation: NetworkAffiliationDetails | null =
    filterNetworkAffiliations(selectedProviderLocation?.networks).find(
      (affiliation) =>
        affiliation.networkAffiliationId === selectedNetworkAffiliationId
    ) || null;

  const {
    line = '',
    city = '',
    state = '',
    postalCode = '',
  } = selectedLocation ? selectedProviderLocation?.address : {};

  const addressString = [
    line,
    city,
    state && `${state} ${shortenZipCode(postalCode)}`,
  ]
    .filter((part) => part)
    .join(', ');

  const { phones, providerGroupsAffiliations } = selectedProviderLocation;
  const primaryPhoneNumber = getPrimaryPhone(
    phones?.phone,
    phones?.appointment
  );
  const networkAffiliationName = capitalizeFirstLetters(
    selectedNetworkAffiliation?.networkAffiliationName
  );
  const providerGroupName = capitalizeFirstLetters(
    providerGroupsAffiliations?.[0]?.value
  );
  const providerNetworkAffiliationMedicalGroupName = capitalizeFirstLetters(
    selectedNetworkAffiliation?.medicalgrpaffilIdName?.[0] || ''
  );

  const [breadcrumbUrls] = useSessionStorage<BreadcrumbSessionStorage>(
    Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
    {}
  );

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: choosePCPSuccess,
    sitesectionLevel1: 'pcp',
  });

  useEffect(() => {
    document.title = `${t(
      'CHOOSE_PCP_FLOW.PRIMARY_CARE_PCP_SUCCESSFULLY_CHOSEN'
    )} | ${Constants.SITE_NAME}`;

    adobePageTrackEvent();
  }, []);

  useEffect(() => {
    choosePCPSuccessRef?.current?.focus();
  }, []);

  useEffect(() => {
    adobeBrowserBackButtonTrack();
  }, []);

  const parseBreadcrumbs = (urls) => {
    const breadcrumbs = [
      ...(urls?.[ConstantsRoutes.PROVIDER_SEARCH_RESULTS.key]
        ? [
            {
              title: t('BC Results'),
              href: urls?.[ConstantsRoutes.PROVIDER_SEARCH_RESULTS.key],
            },
          ]
        : []),
      ...(urls?.[ConstantsRoutes.PROVIDER_DETAILS.key]
        ? [
            {
              title: t('DETAILS_PAGE.PROVIDER_BREADCRUMB'),
              href: urls?.[ConstantsRoutes.PROVIDER_DETAILS.key],
            },
          ]
        : []),
      {
        title: t('PCP_LOCATION.PCP Chosen'),
        href: '',
      },
    ];
    return breadcrumbs;
  };

  const breadcrumbs = parseBreadcrumbs(breadcrumbUrls);

  const choosePCPSuccessPageDetail = (
    <ChooseLocationCardsSuccessContainer>
      <Flex direction="column">
        <SuccessPageYourPCPHeader>
          <Heading
            css={{
              'abyss-heading-root': {
                fontSize: '$mediumText !important',
                marginBottom: '12px',
                lineHeight: '32px !important',
              },
            }}
            display="h6"
            offset={1}
          >
            <IconMaterial
              color="$success1"
              css={{
                'abyss-icon': {
                  marginRight: '6.5px',
                },
              }}
              icon="check"
              size="15px"
            />
            {t('PCP_LOCATION.YOUR_PCP')}
          </Heading>
          <div style={{ marginLeft: 'auto' }}>
            <LinkWithTracking
              analyticsInfo={{
                location: `body:${choosePCPSuccess}`,
              }}
              aria-label={`${t('Change PCP for')} ${formattedProviderName}`}
              before={<IconMaterial icon="edit" size="$sm" />}
              data-auto-testid="success-page-change-pcp-link"
              data-testid="success-page-change-pcp-link"
              isStandardAnchor
              onClick={handleBackToChangePCP}
            >
              <Text
                css={{
                  color: '$info1',
                  fontSize: '$extraSmallText',
                  fontWeight: '$bold',
                  lineHeight: '16px',
                }}
              >
                {t('PCP_LOCATION.CHANGE_PCP')}
              </Text>
            </LinkWithTracking>
          </div>
        </SuccessPageYourPCPHeader>
        <LocationsDropdown cssProps={{ removeTruncatedDataCard }}>
          <Flex
            alignItems={removeTruncatedDataCard ? 'flex-start' : 'center'}
            css={{
              flexWrap: removeTruncatedDataCard ? 'nowrap' : 'wrap',
              gap: removeTruncatedDataCard ? '8px' : '0px',
            }}
          >
            <ChoosePCPPhoto
              css={{
                alignSelf: removeTruncatedDataCard ? 'flex-start' : 'center',
              }}
            >
              {ImageComponent({
                imgRef,
                providerType,
                imgSrc: imageLocation,
                sizeToken: '$choosePCPSuccessIconSize',
              })}
            </ChoosePCPPhoto>
            <Text
              css={{
                lineHeight: '24px',
                'abyss-text-root': {
                  marginLeft: removeTruncatedDataCard ? '0px' : '8px',
                  marginTop: removeTruncatedDataCard ? '8px' : '10px',
                  '@screen < $md': {
                    fontSize: '14.22px',
                  },
                },
              }}
            >
              <TruncatedText
                data-testid="pcp-confirmation-provider-name"
                fontWeight="$bold"
                maxWidth={mobileScreen ? '300px' : '350px'}
                removeTruncatedDataCard={removeTruncatedDataCard}
                size={mobileScreen ? '$md' : '$lg'}
              >
                {formattedProviderName}
              </TruncatedText>

              <Text size={mobileScreen ? '$sm' : '$md'}>
                {primarySpeciality}
              </Text>
            </Text>
          </Flex>
        </LocationsDropdown>
        <SuccessPageYourPCPLocationHeader>
          <Flex direction="row">
            <Heading
              css={{
                'abyss-heading-root': {
                  fontSize: '$mediumText !important',
                  marginBottom: '8px',
                  lineHeight: '32px !important',
                },
              }}
              display="h6"
              offset={1}
            >
              <IconMaterial
                color="$success1"
                css={{
                  'abyss-icon': {
                    marginRight: '6.5px',
                  },
                }}
                icon="check"
                size="15px"
              />
              {t('PCP_LOCATION.YOUR_PCP_LOCATION')}
            </Heading>

            <Layout.Group style={{ marginLeft: 'auto' }}>
              <LinkWithTracking
                analyticsInfo={{
                  location: `body:${choosePCPSuccess}`,
                }}
                aria-label={`${t('Change location for')} ${addressString}`}
                before={<IconMaterial icon="edit" size="$sm" />}
                data-auto-testid="success-page-change-location-link"
                data-testid="success-page-change-location-link"
                onClick={handleBackToChangeLocation}
              >
                <Text
                  css={{
                    color: '$info1',
                    fontSize: '$extraSmallText',
                    fontWeight: '$bold',
                    lineHeight: '16px',
                  }}
                >
                  {t('PCP_LOCATION.CHANGE_LOCATION')}
                </Text>
              </LinkWithTracking>
            </Layout.Group>
          </Flex>
        </SuccessPageYourPCPLocationHeader>
        <ClinicNameAddress>
          <Flex alignItems="center" justify="space-between">
            <ContentContainer>
              <Layout.Stack alignItems="left" grow space="4">
                <div style={{ marginBottom: '12px' }}>
                  {selectedNetworkAffiliation && (
                    <>
                      <Text
                        fontWeight="$bold"
                        size={mobileScreen ? '$md' : '$lg'}
                      >
                        {t('NETWORK_AFFILIATION.NETWORK_AFFILIATION')}:
                      </Text>
                      <TruncatedText
                        data-testid="pcp-confirmation-network-affiliation"
                        maxWidth={mobileScreen ? '300px' : '350px'}
                        size={mobileScreen ? '$sm' : '$md'}
                      >
                        {networkAffiliationName}
                      </TruncatedText>
                    </>
                  )}
                </div>
                <div>
                  <TruncatedText
                    data-testid="pcp-confirmation-provider-group"
                    fontWeight="$bold"
                    maxWidth={mobileScreen ? '300px' : '350px'}
                    removeTruncatedDataCard={removeTruncatedDataCard}
                    size={mobileScreen ? '$md' : '$lg'}
                  >
                    {providerNetworkAffiliationMedicalGroupName ||
                      providerGroupName}
                  </TruncatedText>
                  <TruncatedText
                    maxWidth={mobileScreen ? '300px' : '350px'}
                    removeTruncatedDataCard={removeTruncatedDataCard}
                    size={mobileScreen ? '$sm' : '$md'}
                  >
                    {addressString}
                  </TruncatedText>
                  <Link
                    before={<IconMaterial icon="phone" />}
                    data-testid="pcp-confirmation-phone-number"
                    href={`tel:+1-${primaryPhoneNumber}`}
                    size="md"
                  >
                    {getFormattedPhoneNumber(primaryPhoneNumber)}
                  </Link>
                </div>
              </Layout.Stack>
            </ContentContainer>
          </Flex>
        </ClinicNameAddress>
      </Flex>
      {!isIFPEarlyPay ? (
        <div
          style={{
            justifyContent: 'center',
            flexDirection: 'row',
            marginTop: '31px',
          }}
        >
          <SuccessPageBackButton
            after={
              <IconMaterial
                color="$white"
                css={{ margin: '15px 0 15px -10px' }}
                icon="chevron_right"
                size="24px"
              />
            }
            aria-describedby="reverify"
            data-auto-testid="choose-pcp-back-home"
            data-testid="choose-pcp-back-home"
            onClick={handleBackToFindCare}
          >
            {t('Back to Find Care')}
          </SuccessPageBackButton>
        </div>
      ) : null}
    </ChooseLocationCardsSuccessContainer>
  );

  return (
    <React.Fragment>
      <PCPHeaderContainer data-auto-testid="choose-pcp-success-container">
        <ContentWrapperComponent>
          <SuccessSelectedPCPBox color="#FBFCFE">
            {!mobileScreen ? <Breadcrumb breadcrumbs={breadcrumbs} /> : null}
            <SuccessCheckMark>
              <div>
                <IconBrand
                  color="#FFFFFF"
                  icon="checkmark"
                  size={!mobileScreen ? '40' : '32'}
                  variant={
                    !mobileScreen ? 'twotonelightcircle' : 'twotonedarkcircle'
                  }
                />
              </div>
              <SuccessMessageHeading
                data-auto-testid="success-message-heading"
                data-testid="success-message-heading"
                ref={choosePCPSuccessRef}
                tabIndex={-1}
              >
                <h1
                  aria-label={`${t(
                    'PCP_LOCATION.SUCCESS_LOCATION'
                  )} ${'for'} ${formattedProviderName}`}
                >
                  {t('PCP_LOCATION.SUCCESS_LOCATION')}
                </h1>
                {pcpEffectiveDate ? (
                  <span data-testid="pcp-confirm-effective-date">
                    {t('PCP_LOCATION.SUCCESS_LOCATION_INFO5')}{' '}
                    {isPreEffective ? preEffectivePcpDate : pcpEffectiveDate}.
                  </span>
                ) : null}
                {showPcpLagTimeMessage || isIFPEarlyPay ? (
                  <div data-testid="pcp-change-message">
                    {pcpSuccessMessage?.message}
                  </div>
                ) : null}
              </SuccessMessageHeading>
            </SuccessCheckMark>
          </SuccessSelectedPCPBox>
        </ContentWrapperComponent>
      </PCPHeaderContainer>
      <LocationWrapperComponent>
        <Box
          color="white"
          css={{ position: 'relative' }}
          height="unset"
          padding={0}
        >
          <SuccesssMapViewWrap
            providerLocations={[selectedProviderLocation]}
            successPageDetails={choosePCPSuccessPageDetail}
          />
        </Box>
      </LocationWrapperComponent>
    </React.Fragment>
  );
};
